/**
 * Binärprotokoll für die Bildübertragung.
 *
 * Steuernachrichten laufen als JSON-Textframes, Bilddaten als Binärframes.
 * Beide Binärtypen teilen sich einen 24-Byte-Header, damit der Typ ohne
 * Fallunterscheidung gelesen werden kann.
 */

export const FRAME_DIFF = 1;
export const FRAME_KEY = 2;

export const HEADER_SIZE = 24;

const FLAG_HAS_LOSSY = 1 << 0;
const FLAG_HAS_LOSSLESS = 1 << 1;
const FLAG_LOSSY_IS_JPEG = 1 << 2;
const FLAG_LOSSLESS_IS_PNG = 1 << 3;

export type LossyMime = 'image/webp' | 'image/jpeg';
export type LosslessMime = 'image/webp' | 'image/png';
export type ImageMime = LossyMime | LosslessMime;

function mimeToFlags(mime: ImageMime): number {
  if (mime === 'image/jpeg') return FLAG_LOSSY_IS_JPEG;
  if (mime === 'image/png') return FLAG_LOSSLESS_IS_PNG;
  return 0;
}

function flagsToMime(flags: number): ImageMime {
  if (flags & FLAG_LOSSY_IS_JPEG) return 'image/jpeg';
  if (flags & FLAG_LOSSLESS_IS_PNG) return 'image/png';
  return 'image/webp';
}

/**
 * Ein Differenzframe: bis zu zwei Atlanten mit den geänderten Kacheln.
 * Die Kachelindizes sind row-major (i % countTilesX, i / countTilesX);
 * ihre Position in der Liste ist zugleich der Zellindex im jeweiligen Atlas.
 */
export interface DiffFrame {
  type: typeof FRAME_DIFF;
  frameId: number;
  /** Sendezeitpunkt in ms (Date.now) — für die Latenzmessung. */
  timestamp: number;
  tileDim: number;
  lossyTiles: Uint16Array;
  losslessTiles: Uint16Array;
  lossyData: Uint8Array | null;
  losslessData: Uint8Array | null;
  lossyMime: LossyMime;
  losslessMime: LosslessMime;
}

/**
 * Ein Vollbild — für Late-Join, Reconnect und weit zurückliegende Viewer.
 *
 * Auch der Sender nutzt diesen Typ, wenn nahezu alles geändert ist: Ein Atlas
 * mit allen Kacheln wäre durch das Zellpadding größer als das Vollbild selbst.
 */
export interface KeyFrame {
  type: typeof FRAME_KEY;
  frameId: number;
  timestamp: number;
  tileDim: number;
  width: number;
  height: number;
  data: Uint8Array;
  mime: ImageMime;
}

export type Frame = DiffFrame | KeyFrame;

export function encodeDiffFrame(frame: Omit<DiffFrame, 'type'>): Uint8Array {
  const { lossyTiles, losslessTiles, lossyData, losslessData } = frame;
  const indexBytes = (lossyTiles.length + losslessTiles.length) * 2;
  const lossyLen = lossyData?.length ?? 0;
  const losslessLen = losslessData?.length ?? 0;

  const buf = new Uint8Array(HEADER_SIZE + indexBytes + lossyLen + losslessLen);
  const view = new DataView(buf.buffer);

  let flags = 0;
  if (lossyData) flags |= FLAG_HAS_LOSSY;
  if (losslessData) flags |= FLAG_HAS_LOSSLESS;
  if (frame.lossyMime === 'image/jpeg') flags |= FLAG_LOSSY_IS_JPEG;
  if (frame.losslessMime === 'image/png') flags |= FLAG_LOSSLESS_IS_PNG;

  view.setUint8(0, FRAME_DIFF);
  view.setUint8(1, flags);
  view.setUint16(2, frame.tileDim);
  view.setUint32(4, frame.frameId);
  view.setUint16(8, lossyTiles.length);
  view.setUint16(10, losslessTiles.length);
  view.setUint32(12, lossyLen);
  view.setFloat64(16, frame.timestamp);

  let off = HEADER_SIZE;
  for (const tile of lossyTiles) {
    view.setUint16(off, tile);
    off += 2;
  }
  for (const tile of losslessTiles) {
    view.setUint16(off, tile);
    off += 2;
  }
  if (lossyData) {
    buf.set(lossyData, off);
    off += lossyData.length;
  }
  if (losslessData) {
    buf.set(losslessData, off);
  }
  return buf;
}

export function encodeKeyFrame(frame: Omit<KeyFrame, 'type'>): Uint8Array {
  const buf = new Uint8Array(HEADER_SIZE + frame.data.length);
  const view = new DataView(buf.buffer);

  const flags = mimeToFlags(frame.mime);

  view.setUint8(0, FRAME_KEY);
  view.setUint8(1, flags);
  view.setUint16(2, frame.tileDim);
  view.setUint32(4, frame.frameId);
  view.setUint16(8, frame.width);
  view.setUint16(10, frame.height);
  view.setUint32(12, frame.data.length);
  view.setFloat64(16, frame.timestamp);

  buf.set(frame.data, HEADER_SIZE);
  return buf;
}

export function decodeFrame(bytes: Uint8Array): Frame {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const type = view.getUint8(0);
  const flags = view.getUint8(1);
  const tileDim = view.getUint16(2);
  const frameId = view.getUint32(4);
  const timestamp = view.getFloat64(16);

  if (type === FRAME_KEY) {
    const length = view.getUint32(12);
    return {
      type: FRAME_KEY,
      frameId,
      timestamp,
      tileDim,
      width: view.getUint16(8),
      height: view.getUint16(10),
      data: bytes.subarray(HEADER_SIZE, HEADER_SIZE + length),
      mime: flagsToMime(flags),
    };
  }

  if (type !== FRAME_DIFF) {
    throw new Error(`Unbekannter Frame-Typ: ${type}`);
  }

  const countLossy = view.getUint16(8);
  const countLossless = view.getUint16(10);
  const lossyLen = view.getUint32(12);

  const lossyTiles = new Uint16Array(countLossy);
  const losslessTiles = new Uint16Array(countLossless);
  let off = HEADER_SIZE;
  for (let i = 0; i < countLossy; i++, off += 2) {
    lossyTiles[i] = view.getUint16(off);
  }
  for (let i = 0; i < countLossless; i++, off += 2) {
    losslessTiles[i] = view.getUint16(off);
  }

  const hasLossy = (flags & FLAG_HAS_LOSSY) !== 0;
  const hasLossless = (flags & FLAG_HAS_LOSSLESS) !== 0;

  const lossyData = hasLossy ? bytes.subarray(off, off + lossyLen) : null;
  const losslessData = hasLossless ? bytes.subarray(off + lossyLen) : null;

  return {
    type: FRAME_DIFF,
    frameId,
    timestamp,
    tileDim,
    lossyTiles,
    losslessTiles,
    lossyData,
    losslessData,
    lossyMime: flags & FLAG_LOSSY_IS_JPEG ? 'image/jpeg' : 'image/webp',
    losslessMime: flags & FLAG_LOSSLESS_IS_PNG ? 'image/png' : 'image/webp',
  };
}

/** Liest nur die Frame-Nummer, ohne den Rest zu dekodieren. */
export function peekFrameId(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return view.getUint32(4);
}

/**
 * Trägt die Frame-Nummer nachträglich ein.
 *
 * Die Nummerierung vergibt der Server, denn nur er kennt die raumweite
 * Reihenfolge; der Sender schreibt einen Platzhalter. Weil nur vier
 * Header-Bytes betroffen sind, bleibt das Frame ansonsten unangetastet und
 * kann ohne Re-Encoding weitergereicht werden.
 */
export function setFrameId(bytes: Uint8Array, frameId: number): void {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  view.setUint32(4, frameId);
}

// ---------------------------------------------------------------------------
// Steuernachrichten (JSON)
// ---------------------------------------------------------------------------

/** Rolle beim Verbindungsaufbau. */
export interface HelloMessage {
  t: 'hello';
  role: 'sender' | 'viewer';
  room: string;
}

/** Bildgeometrie — beim Start und bei jedem Auflösungswechsel. */
export interface MetaMessage {
  t: 'meta';
  width: number;
  height: number;
  tileDim: number;
}

/**
 * Empfangsbereitschaft des Viewers samt seines Stands.
 * lastFrameId = 0 bedeutet "noch kein Bild" und löst ein Keyframe aus.
 */
export interface ReadyMessage {
  t: 'ready';
  lastFrameId: number;
}

/** Sender -> Server: die Freigabe beginnt oder endet. */
export interface SharingMessage {
  t: 'sharing';
  active: boolean;
}

/**
 * Sender -> Server: Lebenszeichen.
 * Bleibt der Bildschirm unverändert, entstehen keine Frames – ohne dieses
 * Signal wäre ein stehendes Bild nicht von einem toten Sender zu unterscheiden.
 */
export interface AliveMessage {
  t: 'alive';
}

/**
 * Zeitabgleich, in beide Richtungen dieselbe Nachricht.
 * Der Client schickt `sent`, der Server spiegelt sie mit seiner Uhr in
 * `server` zurück. Siehe shared/clock.ts.
 */
export interface TimeMessage {
  t: 'time';
  sent: number;
  server?: number;
}

/** Server -> Sender: bitte ein Vollbild schicken. */
export interface RequestKeyframeMessage {
  t: 'requestKeyframe';
}

/** Server -> Sender: Ziel-Bildrate, wenn alle Viewer hinterherhinken. */
export interface SetRateMessage {
  t: 'setRate';
  fps: number;
}

/** Server -> alle: Anzahl verbundener Viewer. */
export interface ViewerCountMessage {
  t: 'viewerCount';
  count: number;
}

/** Server -> Sender: Verteilungsstatistik zur Anzeige. */
export interface StatsMessage {
  t: 'stats';
  viewers: {
    id: string;
    behind: number;
    relayed: number;
    coalesced: number;
    keyframes: number;
    lastTileCount: number;
  }[];
}

/**
 * Server -> Viewer: es kommt kein Bild (mehr).
 * Der Grund unterscheidet nur die Anzeige; der Viewer reagiert stets gleich.
 */
export interface SenderGoneMessage {
  t: 'senderGone';
  reason?: 'stopped' | 'timeout' | 'disconnected' | 'idle';
}

export type ClientMessage = HelloMessage | ReadyMessage | MetaMessage | SharingMessage | AliveMessage | TimeMessage;
export type ServerMessage =
  | MetaMessage
  | TimeMessage
  | RequestKeyframeMessage
  | SetRateMessage
  | ViewerCountMessage
  | StatsMessage
  | SenderGoneMessage;
